import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, MenuItem, Select, Typography } from "@mui/material";
import { InfoOutlined, TuneOutlined } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import Tooltip from "../common/Tooltip";
import { EmptyDataState } from "../common/EmptyDataState";
import { getMapImageUrl, getMapMetaById } from "../../constants/mapMeta";
import {
    buildEmptyMapData,
    buildInitialState,
    getMapCompletionPercent,
} from "../../utils/csSkillsState";
import ProfileLoadoutRadar from "./ProfileLoadoutRadar";
import ProfileLoadoutCompletion from "./ProfileLoadoutCompletion";
import ProfileLoadoutSliders from "./ProfileLoadoutSliders";
import utils from "../../universal/utils";

const edgeBasePath = utils.getEdgeBasePath();
const AUTOSAVE_DEBOUNCE_MS = 500;

const FALLBACK_MAP_IDS = [
    "de_mirage",
    "de_inferno",
    "de_nuke",
    "de_ancient",
    "de_dust_ii",
    "de_vertigo",
    "de_cache",
    "de_anubis",
    "de_cobblestone_2018",
];

const LOADOUT_CARD_SX = {
    display: "flex",
    flexDirection: "column",
    borderRadius: "12px",
    position: "relative",
    background: `
        linear-gradient(
            180deg,
            rgba(12,12,14,0.96) 0%,
            rgba(8,8,10,0.985) 100%
        )
    `,
    border: "1px solid rgba(255,255,255,0.05)",
    backdropFilter: "blur(18px)",
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.05),
        inset 0 -40px 80px rgba(246,185,76,0.03),
        0 24px 60px rgba(0,0,0,0.42)
    `,
    gap: "1rem",
    padding: "1.5rem",
    minWidth: 0,
};

const CARD_TOP_GLOW_SX = {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "42%",
    height: "1px",
    background: `
        linear-gradient(
            90deg,
            transparent,
            rgba(246,185,76,0.95),
            transparent
        )
    `,
    boxShadow: "0 0 18px rgba(246,185,76,0.45)",
    pointerEvents: "none",
};

const MAP_SELECT_SX = {
    minWidth: { xs: "9.5rem", sm: "11.5rem" },
    maxWidth: { xs: "100%", sm: "13rem" },
    width: { xs: "100%", sm: "11.5rem" },
    background: colors.background.surface,
    borderRadius: { xs: "8px", sm: "10px" },
    minHeight: { xs: "2.15rem", sm: "2.35rem" },
    padding: { xs: "0.35rem 0.55rem", sm: "0.45rem 0.7rem" },
    transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: colors.border.default,
        borderWidth: "1px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: alpha(colors.primary.main, 0.55),
    },
    "&.Mui-focused": {
        boxShadow: `0 0 0 3px ${alpha(colors.primary.main, 0.1)}`,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: alpha(colors.primary.main, 0.55),
        borderWidth: "1px",
    },
    "& .MuiSelect-select": {
        color: colors.text.primary,
        fontSize: { xs: "0.75rem", sm: "0.8rem" },
        fontFamily: "inherit",
        fontWeight: 600,
        padding: "0 !important",
        display: "flex",
        alignItems: "center",
        minHeight: "unset !important",
        pr: { xs: "1.35rem", sm: "1.5rem" },
    },
    "& .MuiSelect-icon": {
        color: colors.text.muted,
        right: { xs: "0.3rem", sm: "0.45rem" },
        fontSize: "1.15rem",
    },
};

const MAP_SELECT_MENU_PROPS = {
    disableScrollLock: true,
    PaperProps: {
        sx: {
            backgroundColor: colors.background.dark,
            border: `1px solid ${colors.border.default}`,
            borderRadius: "14px",
            mt: "0.35rem",
            boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
            backgroundImage: "none",
        },
    },
    MenuListProps: {
        sx: {
            maxHeight: "18.5rem",
            overflowY: "auto",
            py: 0,
            "& .MuiMenuItem-root": {
                fontSize: { xs: "0.85rem", sm: "0.9rem" },
                minHeight: { xs: "2.4rem", sm: "auto" },
                py: { xs: "0.45rem", sm: "0.55rem" },
                color: colors.text.primary,
                "&.Mui-selected": {
                    backgroundColor: `${alpha(colors.primary.main, 0.24)} !important`,
                    color: colors.accent.main,
                },
                "&.Mui-selected:hover": {
                    backgroundColor: `${alpha(colors.primary.main, 0.32)} !important`,
                },
                "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.05)",
                },
            },
        },
    },
};

function getMapDisplayName(mapId) {
    return getMapMetaById(mapId)?.display || mapId;
}

export default function ProfileLoadoutTab({ catalog, actions, isOwnProfile }) {
    const user = catalog?.user;
    const template = catalog?.csSkillsTemplate;
    const savedSkills = catalog?.csSkills;
    const mapIds = template?.maps?.length
        ? template.maps.map((map) => map.key)
        : FALLBACK_MAP_IDS;

    const [selectedMapId, setSelectedMapId] = useState(mapIds[0]);
    const [localSkills, setLocalSkills] = useState({});
    const localSkillsRef = useRef(localSkills);
    const dirtyRef = useRef(false);
    const saveTimerRef = useRef(null);

    localSkillsRef.current = localSkills;

    useEffect(() => {
        if (!isOwnProfile || !actions) return undefined;
        actions.getCsSkillsTemplate();
        if (user?.userId) {
            actions.getCsSkills({ userId: user.userId });
        }
        return undefined;
    }, [actions, isOwnProfile, user?.userId]);

    useEffect(() => {
        if (!template || dirtyRef.current) return;
        setLocalSkills(buildInitialState(template, savedSkills));
    }, [template, savedSkills]);

    useEffect(() => {
        if (!mapIds.includes(selectedMapId) && mapIds[0]) {
            setSelectedMapId(mapIds[0]);
        }
    }, [mapIds, selectedMapId]);

    const persistSkills = useCallback(() => {
        if (!isOwnProfile || !user?.userId || !actions?.saveCsSkills) return;
        return actions.saveCsSkills({
            userId: user.userId,
            csSkills: { maps: localSkillsRef.current },
        });
    }, [actions, isOwnProfile, user?.userId]);

    const scheduleSave = useCallback(() => {
        if (!isOwnProfile) return;
        dirtyRef.current = true;
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }
        saveTimerRef.current = setTimeout(() => {
            const snapshot = localSkillsRef.current;
            Promise.resolve(persistSkills()).finally(() => {
                if (localSkillsRef.current === snapshot) {
                    dirtyRef.current = false;
                }
            });
        }, AUTOSAVE_DEBOUNCE_MS);
    }, [isOwnProfile, persistSkills]);

    useEffect(() => () => {
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }
        if (dirtyRef.current) {
            persistSkills();
        }
    }, [persistSkills]);

    const selectedMapName = getMapDisplayName(selectedMapId);
    const selectedMapImageUrl = useMemo(
        () => getMapImageUrl(selectedMapId, edgeBasePath),
        [selectedMapId],
    );
    const mapData = localSkills[selectedMapId] || buildEmptyMapData(template);
    const completedMapsCount = useMemo(
        () => mapIds.filter(
            (mapId) => getMapCompletionPercent(localSkills[mapId], template) === 100,
        ).length,
        [localSkills, mapIds, template],
    );

    useEffect(() => {
        if (!selectedMapImageUrl) return undefined;
        const image = new Image();
        image.src = selectedMapImageUrl;
        return undefined;
    }, [selectedMapImageUrl]);

    const updateMapData = useCallback((updater) => {
        setLocalSkills((prev) => {
            const current = prev[selectedMapId] || buildEmptyMapData(template);
            return {
                ...prev,
                [selectedMapId]: updater(current),
            };
        });
        scheduleSave();
    }, [scheduleSave, selectedMapId, template]);

    const handleUtilityGroupChange = useCallback((keys, value) => {
        updateMapData((current) => {
            const nextUtilities = { ...current.utilities };
            keys.forEach((key) => {
                nextUtilities[key] = value;
            });
            return { ...current, utilities: nextUtilities };
        });
    }, [updateMapData]);

    const handleStratChange = useCallback((value) => {
        updateMapData((current) => ({ ...current, stratKnowledge: value }));
    }, [updateMapData]);

    const handleRoleChange = useCallback((roleKey, value) => {
        updateMapData((current) => ({
            ...current,
            roles: { ...current.roles, [roleKey]: value },
        }));
    }, [updateMapData]);

    if (!isOwnProfile) {
        return (
            <EmptyDataState
                title="Loadout is private"
                icon={TuneOutlined}
                minHeight="18rem"
            >
                Skill ratings are only visible on your own profile.
            </EmptyDataState>
        );
    }

    return (
        <Box sx={LOADOUT_CARD_SX}>
            <Box sx={CARD_TOP_GLOW_SX} />
            <Box
                sx={{
                    position: "relative",
                    zIndex: 2,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: "1.15rem",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "stretch", sm: "flex-start" },
                        justifyContent: "space-between",
                        gap: { xs: "0.85rem", sm: "1.25rem" },
                    }}
                >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            sx={{
                                fontSize: "0.8rem",
                                fontWeight: 800,
                                letterSpacing: "0.16em",
                                textTransform: "uppercase",
                                color: colors.text.primary,
                                lineHeight: 1,
                                mb: "0.35rem",
                            }}
                        >
                            Loadout
                        </Typography>
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "0.4rem",
                                maxWidth: "100%",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "0.725rem",
                                    fontWeight: 500,
                                    letterSpacing: "0.04em",
                                    color: "rgba(255,255,255,0.42)",
                                    lineHeight: 1.45,
                                }}
                            >
                                Rate your CS2 skills per map to help us create balanced and competitive matches
                            </Typography>
                            <Tooltip
                                title="Only visible to your teammates during a match."
                                placement="top"
                            >
                                <Box
                                    component="span"
                                    aria-label="Only visible to your teammates during a match."
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                        color: colors.text.muted,
                                        cursor: "help",
                                        transition: "color 0.15s ease-in-out",
                                        "&:hover": {
                                            color: colors.text.secondary,
                                        },
                                    }}
                                >
                                    <InfoOutlined sx={{ fontSize: "0.95rem" }} />
                                </Box>
                            </Tooltip>
                        </Box>
                    </Box>

                    <Select
                        value={mapIds.includes(selectedMapId) ? selectedMapId : mapIds[0]}
                        onChange={(event) => setSelectedMapId(event.target.value)}
                        aria-label="Select map"
                        MenuProps={MAP_SELECT_MENU_PROPS}
                        sx={MAP_SELECT_SX}
                    >
                        {mapIds.map((mapId) => (
                            <MenuItem key={mapId} value={mapId}>
                                {getMapDisplayName(mapId)}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                        gap: "1rem",
                        width: "100%",
                        minWidth: 0,
                        [`@media (min-width: 1400px)`]: {
                            flexDirection: "row",
                            alignItems: "flex-start",
                            gap: "1.5rem",
                        },
                        [`@media (min-width: 2000px)`]: {
                            gap: "2rem",
                        },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.55rem",
                            width: "100%",
                            flexShrink: 0,
                            [`@media (min-width: 1400px)`]: {
                                width: "22rem",
                            },
                            [`@media (min-width: 2000px)`]: {
                                width: "28rem",
                            },
                        }}
                    >
                    <Box
                        sx={{
                            position: "relative",
                            width: "100%",
                            height: "9.5rem",
                            borderRadius: "12px",
                            overflow: "hidden",
                            border: "1px solid rgba(255,255,255,0.06)",
                            boxShadow: `
                                inset 0 1px 0 rgba(255,255,255,0.04),
                                0 12px 28px rgba(0,0,0,0.35)
                            `,
                            backgroundColor: "rgba(0,0,0,0.45)",
                        }}
                    >
                        <Box
                            aria-hidden
                            sx={{
                                position: "absolute",
                                inset: 0,
                                backgroundImage: selectedMapImageUrl
                                    ? `url(${selectedMapImageUrl})`
                                    : "none",
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                backgroundRepeat: "no-repeat",
                                filter: "saturate(0.92) brightness(0.78)",
                            }}
                        />
                        <Box
                            aria-hidden
                            sx={{
                                position: "absolute",
                                inset: 0,
                                background: `
                                    linear-gradient(
                                        180deg,
                                        rgba(0,0,0,0.28) 0%,
                                        rgba(0,0,0,0.42) 42%,
                                        rgba(0,0,0,0.82) 100%
                                    )
                                `,
                                pointerEvents: "none",
                            }}
                        />
                        <Typography
                            sx={{
                                position: "absolute",
                                left: "0.85rem",
                                bottom: "0.7rem",
                                zIndex: 1,
                                fontSize: "0.72rem",
                                fontWeight: 800,
                                letterSpacing: "0.16em",
                                textTransform: "uppercase",
                                color: colors.text.primary,
                                lineHeight: 1,
                                textShadow: "0 2px 10px rgba(0,0,0,0.75)",
                            }}
                        >
                            {selectedMapName}
                        </Typography>
                    </Box>
                    <Typography
                        sx={{
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            letterSpacing: "0.04em",
                            color: "rgba(255,255,255,0.48)",
                            lineHeight: 1.3,
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                color: colors.text.primary,
                                fontWeight: 700,
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {completedMapsCount}/{mapIds.length}
                        </Box>
                        {" "}
                        maps completed
                    </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "stretch", sm: "flex-start" },
                            gap: { xs: "1rem", sm: "1.5rem" },
                            width: "100%",
                            minWidth: 0,
                            [`@media (min-width: 1400px)`]: {
                                flex: 1,
                                gap: "1.75rem",
                            },
                            [`@media (min-width: 2000px)`]: {
                                gap: "2.25rem",
                            },
                        }}
                    >
                        <ProfileLoadoutCompletion
                            mapData={mapData}
                            template={template}
                        />
                        <ProfileLoadoutRadar mapData={mapData} />
                    </Box>
                </Box>

                <ProfileLoadoutSliders
                    template={template}
                    mapData={mapData}
                    onUtilityGroupChange={handleUtilityGroupChange}
                    onStratChange={handleStratChange}
                    onRoleChange={handleRoleChange}
                />
            </Box>
        </Box>
    );
}

ProfileLoadoutTab.propTypes = {
    catalog: PropTypes.object,
    actions: PropTypes.object,
    isOwnProfile: PropTypes.bool,
};
